import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import DateInput from "../DateInput";
import OperativeRouteDialog from "../OperativeRouteDialog";
import { todayLocalISO } from "../../lib/dateUtil";
import { MapTrifold, CarSimple, Warning, ArrowSquareOut, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtDrive = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

/** Properties map → one scheduled visit per picked property for an in-house operative, optimised as a day route. */
export default function RouteDayDialog({ open, onClose, properties, onBooked }) {
  const [team, setTeam] = useState(null);
  const [f, setF] = useState({ operative_id: "", date: todayLocalISO(), day_start: "08:00", duration_minutes: 60, title: "Site visit", instructions: "" });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [showRoute, setShowRoute] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setResult(null);
    API.get("/in-house/team").then(({ data }) => {
      const ops = (data.team || []).filter((u) => u.active !== false);
      setTeam(ops);
      setF((p) => ({ ...p, operative_id: p.operative_id || (ops.length === 1 ? ops[0]._id : "") }));
    }).catch(() => setTeam([]));
  }, [open]);

  const submit = async () => {
    if (!f.operative_id) return toast.error("Choose an operative");
    if (!f.date) return toast.error("Choose a day");
    setSaving(true);
    try {
      const { data } = await API.post("/properties/route-day", { ...f, property_ids: properties.map((p) => p.id) });
      setResult(data);
      toast.success(`${data.jobs.length} visit${data.jobs.length === 1 ? "" : "s"} booked for ${data.operative.name}`);
      onBooked && onBooked(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not book the visits"); }
    finally { setSaving(false); }
  };

  const op = team?.find((u) => u._id === f.operative_id);
  const noHome = op && !op.home_location?.latitude;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="route-day-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-[Chivo]"><MapTrifold size={20} className="text-primary" weight="duotone" /> {result ? "Day planned" : "Plan a day of visits"}</DialogTitle>
          <DialogDescription>{result ? `${result.jobs.length} visit${result.jobs.length === 1 ? "" : "s"} booked for ${result.operative.name} on ${new Date(result.date).toLocaleDateString("en-GB")}.` : `One visit per picked property, assigned to the operative and ordered as their Route Planner would drive it.`}</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3" data-testid="route-day-result">
            {result.no_home && <p className="text-xs rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200 p-2.5 flex gap-2" data-testid="route-day-no-home"><Warning size={14} className="shrink-0 mt-0.5" /> {result.message}</p>}
            {result.plan?.totals && (
              <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="route-day-totals"><CarSimple size={14} /> {result.plan.totals.distance_miles} mi · {fmtDrive(result.plan.totals.drive_minutes)} driving · leave {result.plan.depart_home}, home {result.plan.arrive_home}{result.plan.estimated ? " (estimated)" : ""}</p>
            )}
            <ol className="divide-y divide-border border border-border rounded-md">
              {result.jobs.map((j, i) => (
                <li key={j.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`route-day-stop-${j.id}`}>
                  <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-[11px] font-bold flex items-center justify-center shrink-0">{j.order || i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{j.property_name} <span className="text-muted-foreground font-normal">· {j.client_name}</span></p>
                    <p className="text-[11px] text-muted-foreground truncate">{j.address}</p>
                  </div>
                  <span className="text-xs tabular-nums text-muted-foreground shrink-0">{j.start_time ? `${j.start_time}–${j.end_time}` : "flexible"}</span>
                  <Link to={`/jobs/${j.id}`} className="text-primary hover:underline text-xs inline-flex items-center gap-1 shrink-0" data-testid={`route-day-open-job-${j.id}`}>{j.reference} <ArrowSquareOut size={11} /></Link>
                </li>
              ))}
            </ol>
            {result.skipped?.length > 0 && (
              <div className="text-xs text-amber-800 dark:text-amber-300" data-testid="route-day-skipped">
                <p className="font-medium">Not booked:</p>
                {result.skipped.map((s) => <p key={s.id}>• {s.name} — {s.reason}</p>)}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              {result.plan && <Button variant="outline" size="sm" onClick={() => setShowRoute(true)} data-testid="route-day-view-route"><MapTrifold size={14} className="mr-1" /> View full route</Button>}
              <Button size="sm" onClick={onClose} data-testid="route-day-done"><CheckCircle size={14} className="mr-1" /> Done</Button>
            </div>
            <OperativeRouteDialog open={showRoute} onOpenChange={setShowRoute} operative={result.operative} initialDate={result.date} />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/20 p-2.5 text-xs" data-testid="route-day-picked">
              <p className="font-medium mb-1">{properties.length} propert{properties.length === 1 ? "y" : "ies"} picked</p>
              <p className="text-muted-foreground line-clamp-3">{properties.map((p) => p.name).join(" · ")}</p>
            </div>
            <div>
              <Label className="text-xs">Operative *</Label>
              <select value={f.operative_id} onChange={(e) => set("operative_id", e.target.value)} className="mt-1 w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="route-day-operative">
                <option value="">{team === null ? "Loading…" : team.length ? "Choose an in-house operative" : "No in-house operatives yet"}</option>
                {(team || []).map((u) => <option key={u._id} value={u._id}>{u.name || u.email}{u.home_location?.latitude ? "" : " (no home location)"}</option>)}
              </select>
              {noHome && <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1" data-testid="route-day-operative-no-home">No home location — visits will be booked without an optimised order or times.</p>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Day *</Label><DateInput value={f.date} onChange={(e) => set("date", e.target.value)} className="mt-1" data-testid="route-day-date" /></div>
              <div><Label className="text-xs">Leave home</Label><Input type="time" value={f.day_start} onChange={(e) => set("day_start", e.target.value)} className="mt-1" data-testid="route-day-start" /></div>
              <div><Label className="text-xs">Minutes per visit</Label><Input type="number" min={5} max={480} value={f.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} className="mt-1" data-testid="route-day-duration" /></div>
            </div>
            <div><Label className="text-xs">Visit title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Site visit" className="mt-1" data-testid="route-day-title" /><p className="text-[10px] text-muted-foreground mt-1">Each job is titled “{f.title || "Site visit"} — property name”.</p></div>
            <div><Label className="text-xs">Instructions for the operative</Label>
              <textarea value={f.instructions} onChange={(e) => set("instructions", e.target.value)} rows={2} placeholder="What to check or do at each property…" className="mt-1 w-full text-sm rounded-md border border-border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary" data-testid="route-day-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button size="sm" onClick={submit} disabled={saving || !properties.length} data-testid="route-day-submit">{saving ? "Booking…" : `Book ${properties.length} visit${properties.length === 1 ? "" : "s"}`}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
